import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check } from "lucide-react";

const Plan = ({ name, price, period, promo, features, packageId, onStart, loading }) => (
  <div data-testid={`plan-${packageId}`} className={`bg-white border ${promo ? "border-amber-700" : "border-stone-200"} p-10 rounded-md flex flex-col`}>
    {promo && (
      <div className="text-[11px] uppercase tracking-[0.3em] text-amber-700 font-semibold">Limited Promo</div>
    )}
    <div className="font-serif text-3xl text-stone-900 mt-2">{name}</div>
    <div className="mt-6 flex items-baseline gap-2">
      <span className="font-serif text-5xl text-stone-900">{price}</span>
      <span className="text-stone-500 font-sans text-sm">/{period}</span>
    </div>
    <ul className="mt-8 space-y-3 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-3 font-sans text-stone-700 text-sm">
          <Check className="text-emerald-800 mt-0.5 shrink-0" size={16} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Button
      data-testid={`checkout-${packageId}`}
      onClick={() => onStart(packageId)}
      disabled={loading === packageId}
      className={`mt-8 w-full ${promo ? "bg-amber-700 hover:bg-amber-600" : "bg-emerald-800 hover:bg-emerald-700"} text-stone-50 py-6 rounded-md`}
    >
      {loading === packageId ? "Redirecting..." : "Start Membership"}
    </Button>
  </div>
);

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const startCheckout = async (package_id) => {
    if (!user) {
      toast.info("Sign in first to subscribe.");
      navigate("/login");
      return;
    }
    setLoading(package_id);
    try {
      const { data } = await api.post("/membership/checkout", {
        package_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setLoading(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    }
  };

  const active = user?.membership?.status === "active";

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Membership</div>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900 max-w-3xl">
        Members order for free.
      </h1>
      <p className="mt-4 font-sans text-stone-600 max-w-2xl">
        With a yearly membership, ordering is free across the whole marketplace. Without a membership, modest service fees apply. Membership is the easiest way to keep things simple.
      </p>

      {active && (
        <div data-testid="membership-active" className="mt-6 inline-flex items-center gap-3 bg-emerald-800/10 border border-emerald-800/30 px-4 py-2 rounded-md">
          <Check className="text-emerald-800" size={16} />
          <span className="text-sm font-sans text-emerald-900">You're a {user.membership.label} member — ordering is free.</span>
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <Plan
          packageId="monthly"
          name="Monthly"
          price="$11.99"
          period="mo"
          loading={loading}
          onStart={startCheckout}
          features={[
            "Free ordering while active",
            "Order tracking & feedback",
            "Wholesale onboarding eligible",
            "Refer friends with confirmation",
          ]}
        />
        <Plan
          packageId="promo"
          name="Annual — Founders Promo"
          price="$89.99"
          period="yr"
          promo
          loading={loading}
          onStart={startCheckout}
          features={[
            "Free ordering all year",
            "Founding rate — save $40",
            "Welcome certificate of completion",
            "Direct messaging with hobby farmers",
          ]}
        />
        <Plan
          packageId="annual"
          name="Annual — Standard"
          price="$129.99"
          period="yr"
          loading={loading}
          onStart={startCheckout}
          features={[
            "Free ordering all year",
            "Annual savings vs monthly",
            "Priority on local pickup routes",
            "Direct messaging with hobby farmers",
          ]}
        />
      </div>

      <div className="mt-12 bg-stone-100 border border-stone-200 rounded-md p-8">
        <div className="font-serif text-2xl text-stone-900">Not a member yet?</div>
        <p className="mt-2 font-sans text-stone-600 max-w-3xl">
          You can still browse and order without a membership — modest service fees apply on each order. Joining the membership removes those fees instantly.
        </p>
      </div>

      <p className="mt-6 text-xs text-stone-500 font-sans">
        Payments are processed securely by Stripe. Test mode active in development.
      </p>
    </div>
  );
}

